import { H2HTeamStats, HeadToHeadFirebaseCollection, HeadToHeadGameT } from '../types/game.types';
import { HeadToHeadID, TeamAbrvID } from '../types/id.types';
import { breakHeadToHeadID } from './id-helpers';

/**
 * Merges matchup history by game identity and rebuilds its win/loss/tie totals.
 * Incoming results replace stored versions of the same game, including corrections.
 * Other seasons remain intact and games are ordered oldest first by game ID.
 * Database reads, timestamps and transaction retries belong to the calling writer.
 *
 * @param {HeadToHeadID} headToHeadID - Sorted team pair identifying the matchup
 * @param {HeadToHeadGameT[]} existingGames - Previously stored history
 * @param {HeadToHeadGameT[]} incomingGames - Results to add or replace
 *
 * @return {HeadToHeadFirebaseCollection} Unique history and recalculated nested totals
 */
export const mergeHeadToHeadGames = (
  headToHeadID: HeadToHeadID,
  existingGames: HeadToHeadGameT[],
  incomingGames: HeadToHeadGameT[],
): HeadToHeadFirebaseCollection => {
  const { team1AbrvID, team2AbrvID } = breakHeadToHeadID(headToHeadID);
  const gamesByID = new Map([...existingGames, ...incomingGames].map((game) => [game.gameID, game]));
  const games = [...gamesByID.values()].sort((a, b) => a.gameID.localeCompare(b.gameID));
  const allTimeRecord = { team1Wins: 0, team2Wins: 0, ties: 0 };
  games.forEach((game) => {
    if (game.winnerTeamAbrvID === 'TIE') allTimeRecord.ties++;
    else if (game.winnerTeamAbrvID === team1AbrvID) allTimeRecord.team1Wins++;
    else if (game.winnerTeamAbrvID === team2AbrvID) allTimeRecord.team2Wins++;
  });
  return { headToHeadID, team1AbrvID, team2AbrvID, games, allTimeRecord };
};

/**
 * Presents a stored matchup from the requested team's perspective. Storage uses
 * canonical team order; schedule cards retain the order shown in their score view.
 * Totals include all stored meetings, matching the existing H2H view. Missing
 * data produces zero totals so the History slide retains its layout while loading.
 *
 * @param {HeadToHeadFirebaseCollection | undefined} record - Stored matchup, when available.
 * @param {TeamAbrvID} teamAbrvID - Team whose totals will be displayed.
 * @returns {H2HTeamStats} Record and scoring totals for that team.
 */
export const getHeadToHeadTeamStats = (
  record: HeadToHeadFirebaseCollection | undefined,
  teamAbrvID: TeamAbrvID,
): H2HTeamStats => {
  if (!record) {
    return { wins: 0, losses: 0, ties: 0, pointsScored: 0, pointsAllowed: 0, pointsDiff: 0 };
  }
  const isFirst = record.team1AbrvID === teamAbrvID;
  const firstPoints = record.games.reduce((sum, game) => sum + game.team1Score, 0);
  const secondPoints = record.games.reduce((sum, game) => sum + game.team2Score, 0);
  const pointsScored = isFirst ? firstPoints : secondPoints;
  const pointsAllowed = isFirst ? secondPoints : firstPoints;
  return {
    wins: isFirst ? record.allTimeRecord.team1Wins : record.allTimeRecord.team2Wins,
    losses: isFirst ? record.allTimeRecord.team2Wins : record.allTimeRecord.team1Wins,
    ties: record.allTimeRecord.ties,
    pointsScored,
    pointsAllowed,
    pointsDiff: pointsScored - pointsAllowed,
  };
};
